import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DeviceMeasurement} from "../../../../model/device-measurement";
import {Tenant} from "../../../../model/tenant";
import {translate} from "@ngneat/transloco";
import {PreviousRouteService} from "../../../../services/previous-route.service";
import {MeasurementType} from "../../../../model/measurement-type";
import {TenantsService} from "../../../../services/tenants.service";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {DevicesService} from "../../../../services/devices.service";
import {MessageService} from "primeng/api";
import {DeviceExpressionChecker} from "../../../../model/device-expression-checker";
import {DefaultMeasurementType} from "../../../../model/default-measurement-type";
import {LoggedUser} from '../../../../common/logged-user.service';

@Component({
  selector: 'app-devices-form-expressions',
  templateUrl: './devices-form-expressions.component.html',
  styleUrls: ['./devices-form-expressions.component.scss']
})
export class DevicesFormExpressionsComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() deviceExpressions: DeviceMeasurement[]
  @Output() deviceExpressionsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  measurementExpressionForm: FormGroup;
  rowsForm: FormArray;

  measurements: DeviceMeasurement[];
  expressions: DeviceMeasurement[];

  deviceExpressionChecker: DeviceExpressionChecker;

  tenantMeasurementTypes: (MeasurementType | DefaultMeasurementType)[];
  measurementTypes: (MeasurementType | DefaultMeasurementType)[];
  measurementTypesDisabled = false;
  rowEdit: boolean;
  rowEditIndex: number;

  constructor(private previousService: PreviousRouteService,
              private tenantsService: TenantsService,
              private formBuilder: FormBuilder,
              private devicesService: DevicesService,
              private messageService: MessageService,
              private loggedUser: LoggedUser) {
    this.measurements = [];
    this.expressions = [];
    if (!this.deviceExpressions) this.deviceExpressions = [];
    this.rowsForm = this.formBuilder.array([]);
    this.measurementExpressionForm = this.createExpressionForm();
  }

  ngOnInit(): void {
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantMeasurementTypes(this.tenant.uuid).subscribe(response => {
      this.tenantMeasurementTypes = response.measurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.measurementTypes = [...this.tenantMeasurementTypes];
      this.measurementTypesDisabled = false;
      this.measurements = this.deviceMeasurements;
      if (this.deviceExpressions.length > 0) {
        this.rowsForm = this.formBuilder.array(
          this.deviceExpressions.map(measurement => this.addMeasurementForm(measurement))
        );
      }

      if ('ROLE_OPERATOR' === this.loggedUser.user.role.toUpperCase()) {
        this.measurementExpressionForm.get('name').disable();
        this.measurementExpressionForm.get('description').disable();
        this.measurementExpressionForm.get('alias').disable();
        this.measurementExpressionForm.get('expression').disable();
      }
    });
  }

  private createExpressionForm(): FormGroup {
    const form = this.formBuilder.group({
      name: this.formBuilder.control(null,
        [Validators.maxLength(255),
          Validators.required,
          noSpecialCharacters]),
      description: this.formBuilder.control(null,
        [Validators.maxLength(255)]),
      alias: this.formBuilder.control(null),
      measurementType: this.formBuilder.control(null, Validators.required),
      expression: this.formBuilder.control(null, Validators.required),
      uuid: this.formBuilder.control(null)
    }, {updateOn: 'blur'});
    return form;
  }

  checkExpression(editing?: boolean): void {
    const measurements = [];
    let expression;
    let newMeasurement;
    this.measurements.forEach(measurement => measurements.push(measurement.name));

    expression = this.measurementExpressionForm.get('expression').value;
    newMeasurement = this.measurementExpressionForm;

    this.deviceExpressionChecker = {variableSensors: measurements, expression: expression};
    this.devicesService.checkExpression(this.deviceExpressionChecker).toPromise().then((response) => {
      const valid = response.validExpression;
      if (valid === true) {
        if (!editing) {
          this.rowsForm.push(newMeasurement);
          this.deviceExpressions.push(newMeasurement.value);
          this.measurementExpressionForm = this.createExpressionForm();
        } else {
          this.rowsForm.at(this.rowEditIndex).setValue(this.measurementExpressionForm.value);
          this.deviceExpressions = this.rowsForm.value;
          this.measurementExpressionForm = this.createExpressionForm();
          this.rowEditIndex = null;
          this.rowEdit = false;
        }
      } else {
        const summary = translate('error.edit.expression');
        const detail = translate('error.jexlExpression.expressionErrors');
        this.messageService.add({severity: 'error', summary, detail, sticky: true});
      }
    }, error => {
      const summary = translate('error.edit.expression');
      const detail = translate('error.edit.expressionDetail');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
    });
  }

  addSensor(sensorName: string): void {
    let expression = this.measurementExpressionForm.get('expression').value ?? '';
    expression += sensorName;
    this.measurementExpressionForm.get('expression').setValue(expression);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
    this.deviceExpressions = this.rowsForm.value;
  }

  onRowEdit(rowIndex: number): void {
    if (this.rowsForm.at(rowIndex).value.measurementType instanceof DefaultMeasurementType) {
      this.measurementTypes = [this.rowsForm.at(rowIndex).value.measurementType];
      this.measurementTypesDisabled = true;
    } else {
      this.measurementTypes = [...this.tenantMeasurementTypes];
      this.measurementTypesDisabled = false;
    }
    this.measurementExpressionForm.setValue(this.rowsForm.at(rowIndex).value);
    this.rowEditIndex = rowIndex;
    this.rowEdit = true;
  }

  private addMeasurementForm(measurement: DeviceMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [measurement.description, [Validators.maxLength(255)]],
      alias: [measurement.alias],
      measurementType: [measurement.measurementType, [Validators.required]],
      expression: [measurement.expression, [Validators.required]],
      uuid: [measurement.uuid]
    }, {updateOn: 'blur'});
  }

  onPreviousStep(): void {
    this.previousStep.emit();
    this.deviceExpressionsChange.emit(this.deviceExpressions);
  }

  onNextStep(): void {
    this.nextStep.emit();
    this.deviceExpressionsChange.emit(this.deviceExpressions);
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

}
